// Подписка в интерфейсе: плашка на стартовом экране, окно «Подписка» (тарифы, связь для оплаты, код активации), окно
// «Telegram» (привязка для восстановления пароля), режим «только просмотр» в редакторе без действующей подписки.
// Сервер всё равно проверяет сам: без подписки запись в отчёты - 402 subscription_required (stoweb/billing.py).
import { useEffect, useState } from 'react';
import type { DialogProps } from '../../core/registry';
import { api, authHooks } from '../../core/api';
import { ctx } from '../../core/ctx';
import { appStore } from '../../core/store';
import { Btn, Field, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { askConfirm } from '../../ui/prompts';
import { authStore, checkSession, useAuth, type Subscription } from './session';

export interface Plan { id: string; title: string; days: number; price: number; stars: number; perMonth: number }
interface BotInfo { username: string; online: boolean }
interface PlansInfo { plans: Plan[]; contacts: { telegram: string; vk: string }; bot: BotInfo; subscription: Subscription }

const fmtDay = (ts: number) => new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const rub = (n: number) => n.toLocaleString('ru-RU') + ' ₽';

/** Подписка действует (или не нужна). До загрузки сведений о пользователе - «да»: сервер проверит сам. */
export function subActive(): boolean { return authStore.getState().me?.subscription?.active ?? true; }

/** Нет подписки - показать окно и вернуть true (для кнопок «Новый отчёт», «Импорт» и т. п.). */
export function needSubscription(): boolean {
  if (subActive()) return false;
  void ctx.openDialog('auth.subscription', {});
  return true;
}

/** Текст о сроке для плашки и окна; null - срока нет (пожизненная, администратор). */
function termText(s: Subscription): string | null {
  if (s.exempt || s.lifetime) return null;
  if (s.active && s.until) return `Подписка действует до ${fmtDay(s.until)}`;
  if (s.until) return `Подписка закончилась ${fmtDay(s.until)}`;
  return 'Подписка не оформлена';
}
const actionText = (s: Subscription) => (s.until ? 'Продлить подписку' : 'Оформить подписку');

// ------------------------------------------------------------------------------------------------ плашка
/** Стартовый экран: без подписки - что можно и как оформить; с оплаченной - срок и «Продлить». */
export function SubscriptionBanner() {
  const s = useAuth((a) => a.me?.subscription);
  const text = s ? termText(s) : null;
  if (!s || !text) return null;
  const soon = s.active && !!s.until && s.until - Date.now() / 1000 < 14 * 86400;
  return (
    <div className={'au-sub-banner' + (s.active ? (soon ? ' is-soon' : '') : ' is-off')} data-testid="sub-banner">
      <Icon name={s.active ? 'badge' : 'lock'} size={18} />
      <span className="au-sub-banner-text"><b>{text}.</b>{!s.active && ' Свои отчёты можно смотреть, но создавать и менять их нельзя.'}</span>
      <Btn icon="star" label={actionText(s)} primary={!s.active || soon} onClick={() => void ctx.openDialog('auth.subscription', {})} testid="sub-banner-btn" />
    </div>
  );
}

/** Редактор без подписки: только просмотр. */
export function ReadOnlyBanner() {
  const s = useAuth((a) => a.me?.subscription);
  if (!s || s.active) return null;
  return (
    <div className="au-sub-banner is-off is-bar" data-testid="readonly-banner" role="status">
      <Icon name="lock" size={16} />
      <span className="au-sub-banner-text"><b>Только просмотр:</b> {(termText(s) || '').toLowerCase()}. Изменения не сохраняются.</span>
      <Btn icon="star" label={actionText(s)} primary onClick={() => void ctx.openDialog('auth.subscription', {})} testid="readonly-banner-btn" />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ окно «Подписка»
export function SubscriptionDialog({ close }: DialogProps<boolean>) {
  const sub = useAuth((a) => a.me?.subscription);
  const [info, setInfo] = useState<PlansInfo | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.get<PlansInfo>('/api/billing/plans').then(setInfo).catch((e) => setErr(e.message)); }, []);
  const activate = async () => {
    if (busy || !code.trim()) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api.post<{ days: number; lifetime: boolean; subscription: Subscription }>('/api/me/subscription/activate', { code: code.trim() });
      await checkSession();
      setCode('');
      ctx.toast(r.lifetime ? 'Код принят: подписка теперь пожизненная' : `Код принят: подписка до ${fmtDay(r.subscription.until as number)}`, 'ok', 6000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const s = sub || info?.subscription;
  const free = !!s && (s.lifetime || s.exempt);
  return (
    <Modal title="Подписка" onClose={() => close()} width={680} testid="sub-dialog" footer={<Btn label="Закрыть" onClick={() => close()} />}>
      {s && (
        <p className="au-sub-state" data-testid="sub-state">
          {s.exempt ? 'Администратору подписка не нужна.' : s.lifetime ? <><Icon name="badge" size={16} /> У вас пожизненная подписка.</> : <>{termText(s)}.</>}
        </p>
      )}
      {!free && (
        <>
          <div className="au-plans" data-testid="sub-plans">
            {(info?.plans || []).map((p) => (
              <div key={p.id} className="au-plan" data-testid={'plan-' + p.id}>
                <div className="au-plan-title">{p.title}</div>
                <div className="au-plan-price">{rub(p.price)}</div>
                <div className="au-plan-note">{p.days > 30 ? `${rub(p.perMonth)} в месяц` : ' '}</div>
              </div>
            ))}
          </div>
          <div className="au-sub-pay">
            <p>Напишите мне, какой срок нужен, и я подскажу, как оплатить:</p>
            <div className="au-sub-links">
              {info && <a className="btn" href={info.contacts.telegram} target="_blank" rel="noopener noreferrer" data-testid="sub-telegram"><Icon name="external" size={14} /> Telegram: @MrSpavn</a>}
              {info && <a className="btn" href={info.contacts.vk} target="_blank" rel="noopener noreferrer" data-testid="sub-vk"><Icon name="external" size={14} /> ВКонтакте: mrspavn</a>}
              {info?.bot.online && <a className="btn btn-primary" href={`https://t.me/${info.bot.username}?start=buy`} target="_blank" rel="noopener noreferrer" data-testid="sub-stars"><Icon name="star" size={14} /> Оплатить звёздами в Telegram</a>}
            </div>
          </div>
          <form className="au-sub-code" onSubmit={(e) => { e.preventDefault(); void activate(); }}>
            <Field label="Код активации" hint="Есть код? Введите его: срок прибавится к подписке">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="STO-XXXX-XXXX-XXXX" spellCheck={false} autoComplete="off" data-testid="sub-code" />
            </Field>
            <Btn label="Активировать" primary onClick={() => void activate()} disabled={busy || !code.trim()} testid="sub-activate" />
          </form>
        </>
      )}
      {err && <div className="au-error" role="alert" data-testid="sub-error"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ окно «Telegram»
export function TelegramDialog({ close }: DialogProps<boolean>) {
  const tg = useAuth((a) => a.me?.telegram);
  const [bot, setBot] = useState<BotInfo | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.get<{ bot: BotInfo }>('/api/me/telegram').then((r) => setBot(r.bot)).catch((e) => setErr(e.message)); }, []);
  // пока пользователь привязывает в Telegram - проверяем, не привязался ли
  useEffect(() => {
    if (!waiting || tg?.linked) return undefined;
    const t = window.setInterval(() => { void checkSession(); }, 3000);
    return () => window.clearInterval(t);
  }, [waiting, tg?.linked]);
  const link = async () => {
    setErr('');
    const w = window.open('', '_blank');           // окно - сразу по щелчку, иначе браузер его заблокирует
    try {
      const r = await api.post<{ url: string }>('/api/me/telegram/link');
      if (w) { w.opener = null; w.location.href = r.url; } else location.href = r.url;
      setWaiting(true);
    } catch (e: any) { w?.close(); setErr(e.message); }
  };
  const unlink = async () => {
    if (!await askConfirm({ title: 'Отвязать Telegram?', text: 'Код для сброса пароля больше не будет приходить. Если забудете пароль, восстановить его сможет только администратор.', ok: 'Отвязать', danger: true, testid: 'tg-unlink-confirm' })) return;
    try { await api.del('/api/me/telegram'); await checkSession(); } catch (e: any) { setErr(e.message); }
  };
  return (
    <Modal title="Telegram" onClose={() => close()} width={520} testid="tg-dialog" footer={<Btn label="Закрыть" onClick={() => close()} />}>
      {tg?.linked
        ? <>
          <p data-testid="tg-state"><Icon name="ok" size={16} /> Telegram привязан{tg.username ? <> (<b>@{tg.username}</b>)</> : ''}. Если забудете пароль, на странице входа нажмите «Забыли пароль?»: код придёт сюда.</p>
          <Btn icon="unlink" label="Отвязать" onClick={() => void unlink()} testid="tg-unlink" />
        </>
        : <>
          <p data-testid="tg-state">Привяжите Telegram, чтобы восстановить пароль самостоятельно, если забудете его: код придёт от бота{bot?.username ? <> @{bot.username}</> : ''}.</p>
          <Btn icon="external" label="Привязать Telegram" primary onClick={() => void link()} testid="tg-link" />
          {waiting && <p className="au-muted small" data-testid="tg-waiting">Откройте бота в Telegram и нажмите «Запустить». Ссылка действует 30 минут.</p>}
          {bot && !bot.online && <p className="au-muted small" data-testid="tg-offline">Бот сейчас не отвечает: привязка пройдёт, когда он снова будет на связи.</p>}
        </>}
      {err && <div className="au-error" role="alert"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ только просмотр
/** Без подписки: редактор не редактируется, сохранение не запускается, 402 от сервера показывает окно «Подписка». */
export function setupReadOnly() {
  const apply = () => {
    const a = appStore.getState();
    const ro = !subActive();
    if (a.readOnly !== ro) a.set({ readOnly: ro });
    if (a.editor && !a.editor.isDestroyed && a.editor.isEditable === ro) a.editor.setEditable(!ro);
    if (ro) document.documentElement.setAttribute('data-readonly', '1');
    else document.documentElement.removeAttribute('data-readonly');
  };
  authStore.subscribe(apply);
  appStore.subscribe((s, p) => { if (s.editor !== p.editor) apply(); });
  let shown = 0;
  authHooks.subscription = () => {
    void checkSession();
    if (Date.now() - shown < 5000) return;          // несколько запросов подряд - одно окно
    shown = Date.now();
    void ctx.openDialog('auth.subscription', {});
  };
}
